<template>
    <ModalWrapper
        v-model="newOrderRequestModal"
        scrollable
        persistent
        :max-width="newAgentMode ? 800 : selectTourStep >= 2 ? 1200 : 700"
    >
        <template v-slot:title>
            <div class="d-flex justify-space-between">
                <div v-if="newAgentMode" class="d-flex align-center">
                    <v-btn class="me-3" size="small" elevation="0" @click="newAgentMode = false">
                        <v-icon color="primary"> mdi-arrow-left </v-icon>
                    </v-btn>
                    <span style="word-break: break-word">
                        {{ $t('create_new_agent') }}
                    </span>
                </div>
                <template v-else>
                    <span style="word-break: break-word">
                        {{
                            selectTourStep >= 2 && selectedTourOffer
                                ? selectedTourOffer.info.name
                                : $t('new_reservation_request')
                        }}
                    </span>
                    <span
                        v-if="selectTourStep >= 2 && selectedTourOffer"
                        :class="{'text-warning': isSelectedTourHasSoldOutOffers}"
                    >
                        {{ $filters.price(tourTotalPrice) }}
                    </span>
                </template>
            </div>
        </template>
        <template v-slot:content>
            <div v-show="!newAgentMode">
                <v-form v-if="!selectTourStep" v-model="valid">
                    <v-expand-transition>
                        <client-type-chooser v-model="clientType" :disabled="isClientTypeChooserDisabled" />
                    </v-expand-transition>
                    <v-expand-transition>
                        <private-client
                            v-if="clientType === 'pc'"
                            ref="privateClient"
                            v-model="selectedClient"
                            :_new-client="newClient"
                            :_is-new-client-mode="isNewClientMode"
                            allow-optional-email
                            @change-new-client="newClient = $event"
                            @change-is-new-client-mode="isNewClientMode = $event"
                        />
                        <corporate-client v-if="clientType === 'cc'" ref="corporateClient" v-model="selectedCompany" />
                        <tour-agency
                            v-if="clientType === 'ta'"
                            ref="tourAgency"
                            :_selected-client="selectedClient"
                            :_new-client="newClient"
                            :_new-agent-mode="newAgentMode"
                            :_selected-company="selectedCompany"
                            @change-selected-client="selectedClient = $event"
                            @change-new-agent-mode="newAgentMode = $event"
                            @change-selected-company="selectedCompany = $event"
                            @change-new-client="newClient = $event"
                        />
                    </v-expand-transition>
                    <div class="mt-8 d-flex flex-column">
                        <!--waiting for implementation on the CORE-->
                        <!--<persons-search-autocomplete-->
                        <!--    v-if="clientType === 'cc'"-->
                        <!--    v-model="tripOrganizer"-->
                        <!--    :company-id="selectedCompany?.id"-->
                        <!--    :label="$t('trip_organizer_name')"-->
                        <!--    :show-icon="false"-->
                        <!--    :single-line="false"-->
                        <!--    :disabled="!selectedCompany?.id"-->
                        <!--    data-cy="trip-organizer"-->
                        <!--    class="mb-5"-->
                        <!--/>-->
                        <v-text-field
                            v-model="tripTitle"
                            variant="outlined"
                            :density="'comfortable'"
                            :label="$t('title')"
                            :disabled="disableTripTitle"
                            persistent-placeholder
                            @keyup="tripTitleChange"
                        />
                        <v-textarea
                            v-model="details"
                            variant="outlined"
                            :label="$t('details')"
                            persistent-placeholder
                        />
                    </div>
                </v-form>
                <div v-else>
                    <select-package-tour-form
                        v-if="selectTourStep === 1"
                        v-model="tourFormValid"
                        :search-request="tourSearchRequest"
                    />
                    <package-tour-entries
                        v-else-if="selectTourStep === 2"
                        v-model="loading"
                        :search-request="{
                            ...tourSearchRequest,
                            ...(packageToursClientId && {clientId: packageToursClientId}),
                            ...(packageToursAgentId && {agentId: packageToursAgentId}),
                        }"
                        :citizenship-id="(selectedClient && selectedClient.citizenshipId) || defaultCitizenshipId"
                        @set-tour="tourOffer = $event"
                    />
                    <package-tour-extra-data
                        v-else
                        :selected-tour-entries="selectedTourEntries"
                        :search-request="tourSearchRequest"
                        :prepare-book-requests="prepareBookRequests"
                        :disabled="loading"
                        @change-price="changeExtraPrice"
                    />
                </div>
            </div>
            <person-form
                v-if="selectedCompany && newAgentMode"
                class="px-8 py-8"
                :person-data="{}"
                :departments="departments"
                :role-prefix="selectedCompany.type === 'TOUR_AGENCY' ? 'ta' : 'to'"
                :company-id="selectedCompany.id"
                :custom-roles="[
                    'accountant',
                    'agent',
                    'director',
                    'guest',
                    'manager',
                    'sales_manager',
                    'sales_staff',
                    'supervisor',
                ]"
                allow-no-credentials
                @save="onSavePersonForm"
            />
            <v-dialog v-model="soldOutNotification" width="800">
                <v-card>
                    <v-card-text class="text-center">
                        <i18n-t keypath="tours.notification.has_sold_out_offers">
                            <template v-slot:link>
                                <a href="#" @click.stop="back">{{ $t('tours.has_sold_out_offers_link') }}</a>
                            </template>
                        </i18n-t>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <prepare-book-messages-modal
                v-if="prepareBookResponse"
                :prepare-book-response="prepareBookResponse"
                :offers-data="offersData"
                is-saving-without-details
                @approve="book(orderId)"
                @cancel="cancelBook(orderId)"
            />
        </template>
        <template v-slot:btns>
            <div class="d-flex full-width" :class="{'flex-column': isMobile}">
                <div class="d-flex align-center" :class="{'mb-4': isMobile}">
                    <v-btn
                        v-if="isToursActive && !selectTourStep && $conf.public.account.newReservationRequestFromPackage"
                        variant="text"
                        color="primary"
                        :disabled="!valid"
                        :loading="loading"
                        @click="selectTourStep++"
                    >
                        {{ $t('tours.select_package') }}
                    </v-btn>
                    <v-btn v-else-if="isToursActive && selectTourStep" variant="text" :disabled="loading" @click="back">
                        {{ $t('back') }}
                    </v-btn>
                    <new-order-request-note :tour="tourOffer" />
                </div>
                <div class="d-flex align-center full-width">
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="flat"
                        :disabled="
                            loading ||
                            (selectTourStep === 0 && !valid) ||
                            (selectTourStep === 1 && !tourFormValid) ||
                            (selectTourStep === 2 && selectedTourEntries.length === 0)
                        "
                        :loading="loading"
                        @click="
                            selectTourStep === 1
                                ? searchPackageTour()
                                : isSelectTourStepWithFlight
                                  ? fillExtraData()
                                  : createRequest()
                        "
                    >
                        {{ selectTourStep === 1 || isSelectTourStepWithFlight ? $t('next') : $t('submit') }}
                    </v-btn>
                </div>
            </div>
        </template>
    </ModalWrapper>
</template>

<script lang="ts">
    import {Component, mixins, Setup, VModel, Watch} from 'vue-facing-decorator'
    import ClientAutocomplete from '@/components/account/modals/newOrderRequest/ClientAutocomplete.vue'
    import CompanyAutocomplete from '@/components/account/modals/newOrderRequest/CompanyAutocomplete.vue'
    import CorporateClient from '@/components/account/modals/newOrderRequest/forms/CorporateClient.vue'
    import PrivateClient from '@/components/account/modals/newOrderRequest/forms/PrivateClient.vue'
    import TourAgency from '@/components/account/modals/newOrderRequest/forms/TourAgency.vue'
    import SelectPackageTourForm from '@/components/account/modals/newOrderRequest/forms/SelectPackageTourForm.vue'
    import {searchRequest} from '@/utils/tours/tours-blank-states'
    import PackageTourEntries from '@/components/account/modals/newOrderRequest/PackageTourEntries.vue'
    import {authorizationPersonRsTransformer} from '@/stores/AuthStore'
    import {EventBus, SHOW_NOTIFICATIONS} from '@/utils/event-bus'
    import PersonForm from '~src/components/account/forms/personForm.src'
    import NewOrderRequestNote from '~src/components/account/modals/newOrderRequest/snippets/NewOrderRequestNote.vue'
    import PackageTourExtraData from '@/components/account/modals/newOrderRequest/PackageTourExtraData.vue'
    import {sumPrice} from '@/utils/api-helpers'
    import {AuthInfoMixin} from '~src/mixins/authInfoMixin.src'
    import ClientTypeChooser from '~src/components/account/snippets/ClientTypeChooser.vue'
    import {tourEntrySelectedOffer} from '~/utils/tours/tour-helpers'
    import PrepareBookMessagesModal from '~src/components/booking/modals/PrepareBookMessagesModal.vue'
    import {isPrepareBookResultClean, prepareBookResult} from '~/utils/PrepareBookAdapter'
    // import PersonsSearchAutocomplete from '@/components/search/forms/PersonsSearchAutocomplete.vue'

    const tourSearchRequest = () => {
        const {adults, childrenAges, durationFrom, durationTo, ...sr} = searchRequest()
        sr.rooms = [hotelsRuntimeStore.roomQueryString({adults, childrenAges})]
        sr.startDateTo = sr.startDateFrom
        sr.departureAirportCode = null
        return sr
    }

    @Component({
        components: {
            PrepareBookMessagesModal,
            ClientTypeChooser,
            PackageTourExtraData,
            NewOrderRequestNote,
            PersonForm,
            PackageTourEntries,
            SelectPackageTourForm,
            TourAgency,
            PrivateClient,
            CorporateClient,
            ClientAutocomplete,
            CompanyAutocomplete,
            // PersonsSearchAutocomplete,
        },
    })
    export class NewOrderRequestModal extends mixins(AuthInfoMixin) {
        @Setup(() => useLocalized()) localized: Function

        @VModel() newOrderRequestModal

        loading = false
        clientType = ''
        selectedCompany = null
        selectedClient = null
        tripTitle = ''
        // tripOrganizer = null
        details = null
        isNewClientMode = false
        newClient = {
            prefix: null,
            firstName: null,
            lastName: null,
            contactPhone: null,
            email: null,
        }
        valid = false
        selectTourStep = 0
        tourFormValid = false
        tourSearchRequest = tourSearchRequest()
        newAgentMode = false
        tourOffer = null
        prepareBookRequests = []
        prepareBookResponse = null
        offersData = []
        extraPrices = []
        orderId = null
        isClientTypeChooserDisabled = false
        soldOutNotification = false
        departments = []

        @Watch('newOrderRequestModal')
        async onDialogToggle() {
            this.resetSelected()
            this.newAgentMode = false

            await this.$nextTick()

            if (this.newOrderRequestModal) {
                await this.initPreselectedValues()
            }
        }

        async initPreselectedValues() {
            if (persistentStore.selectedPrivateClient) {
                this.clientType = 'pc'

                await this.$nextTick()

                if (this.$refs?.privateClient?.$refs?.clientAutocomplete?.clients) {
                    this.$refs.privateClient.$refs.clientAutocomplete.clients = [persistentStore.selectedPrivateClient]
                }

                this.selectedClient = persistentStore.selectedPrivateClient
            }
            if (persistentStore.selectedCompanyAndAgent) {
                if (persistentStore.selectedCompanyAndAgent.company.type === 'CORPORATOR') {
                    this.clientType = 'cc'

                    await this.$nextTick()

                    if (this.$refs?.corporateClient?.$refs?.companyAutocomplete?.clients) {
                        this.$refs.corporateClient.$refs.companyAutocomplete.clients = [
                            persistentStore.selectedCompanyAndAgent.company,
                        ]
                    }

                    this.selectedCompany = persistentStore.selectedCompanyAndAgent.company
                } else {
                    this.clientType = 'ta'

                    await this.$nextTick()

                    if (this.$refs?.tourAgency?.$refs?.companyAutocomplete?.clients) {
                        this.$refs.tourAgency.$refs.companyAutocomplete.clients = [
                            persistentStore.selectedCompanyAndAgent.company,
                        ]
                    }

                    this.selectedCompany = persistentStore.selectedCompanyAndAgent.company

                    await this.$nextTick()

                    if (
                        persistentStore.selectedCompanyAndAgent.agent &&
                        this.$refs?.tourAgency?.$refs?.clients?.clients
                    ) {
                        this.$refs.tourAgency.$refs.clients.clients = [persistentStore.selectedCompanyAndAgent.agent]
                        await this.$refs.tourAgency.$refs.clients.onInput(
                            null,
                            `${persistentStore.selectedCompanyAndAgent.agent.firstName || ''} ${
                                persistentStore.selectedCompanyAndAgent.agent.lastName || ''
                            }`.trim()
                        )
                    }

                    this.selectedClient = persistentStore.selectedCompanyAndAgent.agent
                }
            }
        }

        @Watch('clientType')
        onClientTypeChange() {
            this.resetSelected()
            if (this.clientType === 'ta') {
                this.loadTAData()
            }
        }

        async loadTAData() {
            await runtimeStore.loadAvailablePersonRoles('TOUR_AGENCY')
            if (authStore.isTO1 || authStore.isTO2) await this.loadDepartments()
        }

        async loadDepartments() {
            try {
                const {departments} = await this.$api.departments.get()
                this.departments = departments.map(d => ({
                    ...d,
                    nameOriginal: d.name,
                    name: this.localized(d.name),
                }))
            } catch (e) {
                this.departments = []
            }
        }

        @Watch('selectedTourEntries.length')
        initPrepareBookRequests() {
            this.prepareBookRequests = this.selectedTourEntries.map(
                () =>
                    new Proxy(
                        {},
                        {
                            set: (target, prop, val) => {
                                target[prop] = val
                                return true
                            },
                            get: (target, prop) => {
                                return target[prop]
                            },
                        }
                    )
            )
            this.extraPrices = this.selectedTourEntries.map(() => ({
                amount: 0,
                currency: this.tourSearchRequest.convertToCurrency,
                originalAmount: 0,
            }))
        }

        async mounted() {
            this.resetSelected()
        }

        searchPackageTour() {
            this.tourSearchRequest.convertToCurrency = persistentStore.currency
            this.tourSearchRequest.startDateTo = this.tourSearchRequest.startDateFrom
            this.selectTourStep++
        }

        fillExtraData() {
            if (this.isSelectedTourHasSoldOutOffers && !this.isTO1) {
                this.soldOutNotification = true
                return
            }
            this.selectTourStep++
        }

        async createRequest() {
            if (this.isSelectedTourHasSoldOutOffers && !this.isTO1) {
                this.soldOutNotification = true
                return
            }
            try {
                this.loading = true

                if (this.isNewClientMode) {
                    const rs = await this.$api.registration.post({
                        ...this.newClient,
                        directSales: true,
                        companyId: this.selectedCompany?.id,
                    })
                    this.selectedClient = authorizationPersonRsTransformer(rs)
                }

                const rq = {
                    title: this.tripTitle,
                    comment: this.details,
                    clientPriceType: 'ONLY_TOTAL',
                }

                if (this.selectedTourOffer) {
                    rq.title = rq.title
                        ? `${rq.title} (${this.selectedTourOffer.info.name})`
                        : this.selectedTourOffer.info.name
                }

                if (this.clientType === 'pc') {
                    rq.clientId = this.selectedClient.personId || this.selectedClient.id
                } else if (this.clientType === 'cc') {
                    rq.clientId = this.selectedCompany.id
                    // if (this.tripOrganizer.personId) rq.tripOrganizerId = this.tripOrganizer.personId
                } else if (this.clientType === 'ta') {
                    rq.agentCompanyId = this.selectedClient.companyId
                    rq.agentId = this.selectedClient.personId
                    if (this.newClient.personId) {
                        rq.clientId = this.newClient.personId
                    } else {
                        rq.firstName = this.newClient.firstName
                        rq.lastName = this.newClient.lastName
                        rq.prefix = this.newClient.prefix
                    }
                } else if (this.isCorporate) {
                    rq.clientId = authStore.company.id
                } else if (authStore.person) {
                    rq.clientId = authStore.person.personId
                }

                const {
                    orders: [{orderId}],
                } = await this.$api.orders.post(rq)

                if (this.selectedTourEntries.length) {
                    await this.addTourServices(orderId)
                } else {
                    await this.finishRequest(orderId)
                }
            } catch (e) {
                console.error(e)
                this.$toast.error(e.errors?.[0]?.message || e)
                this.loading = false
            }
        }

        async finishRequest(orderId) {
            if (!authStore.person || this.isCorporate || this.isB2C) {
                this.newOrderRequestModal = false
                this.$toast.success(this.$t('new_reservation_request_success'))
            } else {
                await this.$router.push({name: 'order-details', params: {id: orderId}})
                if (this.isSelectedTourHasSoldOutOffers) {
                    this.$toast.warning(this.$t('tours.notification.saved_order_with_sold_out_services'), {
                        timeout: 15000,
                    })
                }
            }
            this.$emit('request-created')
            this.loading = false
        }

        resetSelected() {
            this.isClientTypeChooserDisabled = false
            this.selectedClient = null
            this.selectedCompany = null
            this.isNewClientMode = false
            this.newClient = {
                firstName: null,
                lastName: null,
                contactPhone: null,
                email: null,
            }
            if (!authStore.person) {
                this.isNewClientMode = true
            }
            this.selectTourStep = 0
            toursRuntimeStore.SET_OFFERS([])
            this.tourSearchRequest = tourSearchRequest()
        }

        tripTitleChange() {}

        async addTourServices(orderId) {
            try {
                const {basketKey} = await this.$api.basket.post()
                this.basketKey = basketKey
                this.orderId = orderId
                this.offersData = []
                const basketRequests = this.selectedTourEntries.reduce((basketRequests, entry, index) => {
                    const prebookRequest = {
                        ...this.prepareBookRequests[index],
                        orderId,
                        offerKey: entry.selectedOfferKey,
                        orderNumber: index + 1,
                    }

                    const offer =
                        entry.type === 'ACCOMMODATION'
                            ? !entry.obligatory && !entry.selectedOfferKey
                                ? entry.offers[0]
                                : hotelsRuntimeStore.findHotelByOfferKey(entry.offers, entry.selectedOfferKey)
                            : entry.offer || entry.offers[0]

                    const {supplierCode, supplierId, info, regular} = offer

                    this.offersData.push({
                        supplierCode:
                            supplierCode || supplierId || info?.supplierCode || (regular === false ? '' : 'company.'),
                        name: offer.name || entry.name,
                        serviceType: entry.type,
                    })
                    switch (entry.type) {
                        case 'ACCOMMODATION':
                            basketRequests.push(
                                this.$api.prepareAccommodationBook.put(
                                    basketKey,
                                    !entry.selectedOfferKeys
                                        ? [prebookRequest]
                                        : entry.selectedOfferKeys.map(offerKey => ({orderId, offerKey}))
                                )
                            )
                            break
                        case 'ACTIVITY':
                        case 'EXCURSION':
                        case 'EVENT':
                            basketRequests.push(this.$api.prepareActivityBook.put(basketKey, prebookRequest))
                            break
                        case 'FLIGHT':
                        case 'OWNCHARTER':
                            basketRequests.push(this.$api.prepareFlightBook.put(basketKey, prebookRequest))
                            break
                        case 'CAR':
                            if (carsRentStore.pickUpLocation?.locationCode)
                                prebookRequest.pickUpPoint = carsRentStore.pickUpLocation.locationCode
                            if (carsRentStore.dropOffLocation?.locationCode)
                                prebookRequest.dropOffPoint = carsRentStore.dropOffLocation.locationCode
                            basketRequests.push(this.$api.prepareCarBook.put(basketKey, prebookRequest))
                            break
                        case 'TRANSFER':
                            basketRequests.push(this.$api.prepareBookTransfer.put(basketKey, prebookRequest))
                            break
                        case 'OWNEXTRASERVICE':
                            basketRequests.push(this.$api.prepareOwnExtraServiceBook.put(basketKey, prebookRequest))
                            break
                        case 'CRUISE':
                            basketRequests.push(this.$api.prepareBookCruise.put(basketKey, prebookRequest))
                            break
                        case 'TOUR':
                            basketRequests.push(this.$api.prepareBookTour.put(basketKey, prebookRequest))
                            break
                    }
                    return basketRequests
                }, [])
                if (basketRequests.length) {
                    const result = await Promise.all(basketRequests)
                    const prepareBookResponse = {
                        prepareBookResponses: result.flat().map(({initResponse}) => initResponse),
                    }
                    if (isPrepareBookResultClean(prepareBookResult(prepareBookResponse, this.offersData))) {
                        await this.book(orderId)
                    } else {
                        this.prepareBookResponse = prepareBookResponse
                    }
                } else {
                    await this.book(orderId)
                }
            } catch (e) {
                console.error(e)
                EventBus.$emit(SHOW_NOTIFICATIONS, {
                    notifications: [this.$t('warning_message.services_not_added_to_new_order_request')],
                })
            }
        }

        async book(orderId) {
            try {
                const {
                    param: {paymentMethod},
                } = await this.$api.paymentOptions.get({basketKey: this.basketKey})
                const {id: paymentMethodId} = paymentMethod.find(paymentMethod => !paymentMethod.book)
                await this.$api.book.post({
                    basketKey: this.basketKey,
                    paymentMethodId,
                })
            } catch (e) {
                console.error(e)
                EventBus.$emit(SHOW_NOTIFICATIONS, {
                    notifications: [this.$t('warning_message.services_not_added_to_new_order_request')],
                })
            } finally {
                await this.finishRequest(orderId)
            }
        }

        async cancelBook(orderId) {
            try {
                await this.$api.orders.put(orderId, {active: false})
            } catch (e) {
                /* empty */
            }
            this.prepareBookResponse = null
            this.loading = false
        }

        changeExtraPrice({price, index}) {
            this.extraPrices[index] = price
        }

        back() {
            this.soldOutNotification = false
            this.selectTourStep--
            this.tourOffer = null
        }

        onSavePersonForm(newUser) {
            this.selectedClient = newUser
            this.newAgentMode = false
        }

        get packageToursClientId() {
            if (this.clientType === 'pc') {
                return null
            }

            return this.selectedCompany?.id
        }

        get packageToursAgentId() {
            if (this.clientType === 'pc') {
                return null
            }

            return this.selectedClient?.personId
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isB2C() {
            return authStore.isB2C
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isAgency() {
            return authStore.isAgency
        }

        get disableTripTitle() {
            return false
        }

        get isToursActive() {
            return this.activeProducts.includes('tours')
        }

        get tourTotalPrice() {
            const tourTotalPrice = toursRuntimeStore.totalPrice(this.selectedTourOffer)
            if (this.extraPrices.length) {
                return sumPrice([tourTotalPrice, ...this.extraPrices])
            }
            return tourTotalPrice
        }

        get selectedTourOffer() {
            return toursRuntimeStore.offers[0]
        }

        get selectedTourEntries() {
            return this.selectedTourOffer?.entries.filter(({selectedOfferKey}) => selectedOfferKey) || []
        }

        get isSelectTourStepWithFlight() {
            return this.selectTourStep === 2 && this.selectedTourEntries.findIndex(({type}) => type === 'FLIGHT') !== -1
        }

        get isSelectedTourHasSoldOutOffers() {
            return !this.selectedTourEntries?.every(entry => !tourEntrySelectedOffer(entry)?.soldOut)
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get defaultCitizenshipId() {
            return authStore.defaultCitizenshipId
        }
    }

    export default NewOrderRequestModal
</script>
